import { useState, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  Input,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  FormControl,
  FormErrorMessage,
  useOutsideClick,
  InputLeftElement,
  InputGroup,
  Portal,
  PopoverArrow,
  List,
  VStack,
  Text,
  ListItem,
} from '@chakra-ui/react';
import { HexColorPicker } from 'react-colorful';
import { CustomColorPicker } from '../../../theme/icons/CustomColorPicker';
import CanvasEyeDropper from './CanvasEyeDropper';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import { setDesignColor } from '../../../store/redux/slices/template.slice';
import { setFormField } from '../../../store/redux/slices/form.slice';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { LabelWithTooltip } from './LabelWithTooltip';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import { FormState } from '../../../store/types/form';
import { ColorsState } from '../../../store/types/template';

interface ColorPickerFieldProps {
  label: string;
  color: string;
  field:
    | 'passBackgroundColor'
    | 'titleFontColor'
    | 'bodyFontColor'
    | 'footerColor'
    | 'backgroundColor'
    | 'componentFillColor'
    | 'componentFontColor'
    | 'buttonText'
    | 'buttonCtaColor'
    | 'buttonColor';
  tooltip?: string;
  namespace?: 'colors' | 'form';
  isDisabled?: boolean;
}

const ColorPickerField = ({
  label,
  color,
  field,
  tooltip,
  namespace = 'colors',
  isDisabled = false,
}: ColorPickerFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const colorPickerPopoverRef = useRef<HTMLDivElement>(null);
  const eyeDropperPopoverRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const images = useAppSelector((s) => s.template.images);
  const { shared, apple, google } = images;
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    prefix: namespace === 'form' ? undefined : namespace,
    fieldRefMap: { [field]: colorPickerRef },
  });
  const [isEyeDropperMenuOpen, setIsEyeDropperMenuOpen] = useState(false);
  const [isEyeDropperOpen, setIsEyeDropperOpen] = useState(false);
  const [dropperImage, setDropperImage] = useState<string | null>(null);
  const form = useAppSelector((s) => s.form);

  useOutsideClick({
    ref: colorPickerPopoverRef,
    handler: () => setIsOpen(false),
  });

  useOutsideClick({
    ref: eyeDropperPopoverRef,
    handler: () => {
      setIsEyeDropperMenuOpen(false);
      setIsEyeDropperOpen(false);
    },
  });

  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation(namespace);
  const errors = useAppSelector((s) => s.templateErrors);

  const handleChange = (
    field:
      | 'passBackgroundColor'
      | 'titleFontColor'
      | 'bodyFontColor'
      | 'footerColor'
      | 'backgroundColor'
      | 'componentFillColor'
      | 'componentFontColor'
      | 'buttonText'
      | 'buttonCtaColor'
      | 'buttonColor',
    value: string
  ) => {
    const upperValue = value.toUpperCase();

    if (namespace === 'form') {
      const updatedForm = { ...form, [field]: upperValue };
      dispatch(
        setFormField({ field: field as keyof FormState, value: upperValue })
      );
      validateField(field, upperValue, { form: updatedForm });
    } else {
      dispatch(
        setDesignColor({ field: field as keyof ColorsState, value: upperValue })
      );
      validateField(field, upperValue);
    }
  };

  const handleEyeDropperMenuItemClick = (image: string) => {
    setDropperImage(image);
    handleEyeDropperOpen();
  };

  const imageMenuItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      image: string | undefined;
    }> = [
      { id: 'shared-logo', label: 'Shared logo image', image: shared.logo },
      { id: 'apple-logo', label: 'Apple logo image', image: apple?.logo },
      { id: 'google-logo', label: 'Google logo image', image: google?.logo },
      { id: 'shared-icon', label: 'Shared icon image', image: shared.icon },
      { id: 'apple-icon', label: 'Apple icon image', image: apple?.icon },
      { id: 'google-icon', label: 'Google icon image', image: google?.icon },
      {
        id: 'apple-thumbnail',
        label: 'Apple thumbnail image',
        image: apple?.thumbnail,
      },
      {
        id: 'shared-banner',
        label: 'Shared banner image',
        image: shared.banner,
      },
      { id: 'apple-banner', label: 'Apple banner image', image: apple?.banner },
      {
        id: 'google-banner',
        label: 'Google banner image',
        image: google?.banner,
      },
    ];
    return items.filter((item) => item.image);
  }, [shared, apple, google]);

  const handleEyeDropperOpen = () => {
    setIsEyeDropperOpen(!isEyeDropperOpen);
    setIsEyeDropperMenuOpen(false);
  };

  const errorPath = `${namespace}.${field}`;

  return (
    <FormControl isInvalid={!!errors[errorPath]}>
      <LabelWithTooltip label={label} tooltip={tooltip} />
      <FieldEditIndicator
        fieldRef={colorPickerRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="25px"
      >
        <Flex gap={6} align="center">
          {/* Input field */}
          <InputGroup maxW="160px">
            <InputLeftElement
              pointerEvents="none"
              fontWeight="bold"
              width="40px"
            >
              #
            </InputLeftElement>
            <Input
              value={color.replace(/^#/, '')}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Fa-f0-9]/g, '');
                handleChange(field, `#${value}`);
              }}
              placeholder="FFFFFF"
              onFocus={() => handleFocus(field)}
              pl="30px"
              isDisabled={isDisabled}
            />
          </InputGroup>

          {/* Color preview and picker */}
          <Flex align="center">
            <Popover
              isOpen={isOpen}
              onClose={() => {
                setIsOpen(false);
                handleBlur();
              }}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor="gray.300"
                  bg={color || 'transparent'}
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  opacity={isDisabled ? 0.6 : 1}
                  pointerEvents={isDisabled ? 'none' : 'auto'}
                  onClick={() => {
                    if (isDisabled) return;
                    if (!isOpen) handleFocus(field);
                    setIsOpen(!isOpen);
                  }}
                />
              </PopoverTrigger>
              <PopoverContent
                ref={colorPickerPopoverRef}
                w="220px"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="md"
              >
                <PopoverBody p={3}>
                  <HexColorPicker
                    color={color}
                    onChange={(value) => handleChange(field, value)}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>

            <Popover placement="top-start" isOpen={isEyeDropperMenuOpen}>
              <PopoverTrigger>
                <Box alignSelf="center">
                  <Icon
                    as={CustomColorPicker}
                    boxSize="20px"
                    color="grey"
                    _hover={isDisabled ? {} : { color: 'orangePrimary' }}
                    ml={2}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    opacity={isDisabled ? 0.6 : 1}
                    pointerEvents={isDisabled ? 'none' : 'auto'}
                    onClick={() =>
                      !isDisabled &&
                      setIsEyeDropperMenuOpen(!isEyeDropperMenuOpen)
                    }
                  />
                </Box>
              </PopoverTrigger>
              <Portal>
                <PopoverContent
                  ref={eyeDropperPopoverRef}
                  boxShadow="0 6px 12px rgba(0, 0, 0, 0.15)"
                  overflow="hidden"
                  maxWidth="200px"
                >
                  <PopoverArrow />
                  <PopoverBody>
                    <Popover isOpen={isEyeDropperOpen}>
                      <PopoverTrigger>
                        <VStack align="flex-start">
                          <Text
                            fontSize="14px"
                            fontFamily="Inter Bold"
                            px={2}
                            pt={2}
                          >
                            Choose an image to pick a colour from:
                          </Text>
                          {imageMenuItems.length > 0 ? (
                            <List width="full" mt={1}>
                              {imageMenuItems.map((item) => (
                                <ListItem
                                  key={item.id}
                                  fontSize="14px"
                                  textStyle="bodyFamilyMedium"
                                  p={2}
                                  _hover={{ bg: 'quinaryBackground' }}
                                  cursor="pointer"
                                  onClick={() =>
                                    handleEyeDropperMenuItemClick(item.id)
                                  }
                                >
                                  {item.label}
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Text
                              fontSize="14px"
                              textStyle="bodyFamilyMedium"
                              px={2}
                              py={2}
                              color="gray.500"
                            >
                              No images available
                            </Text>
                          )}
                        </VStack>
                      </PopoverTrigger>
                      <Portal>
                        <PopoverContent
                          boxShadow="0 6px 12px rgba(0, 0, 0, 0.15)"
                          overflow="hidden"
                          w="auto"
                        >
                          <PopoverArrow />
                          <PopoverBody p={4}>
                            <CanvasEyeDropper
                              color={color}
                              onChange={(value) => handleChange(field, value)}
                              dropperImage={dropperImage}
                            />
                          </PopoverBody>
                        </PopoverContent>
                      </Portal>
                    </Popover>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </Flex>
        </Flex>
      </FieldEditIndicator>

      {errors[errorPath] && (
        <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
          <CustomErrorIcon />
          {errors[errorPath]}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default ColorPickerField;
